'use client';

import { useState, useEffect, useLayoutEffect, useCallback } from 'react';

export function useScrollReveal(threshold = 0.15) {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [revealed, setRevealed] = useState(false);

  // Callback ref: fires when the element mounts/unmounts (e.g. layout switch)
  const ref = useCallback((node: HTMLDivElement | null) => {
    setEl(node);
  }, []);

  // Check visibility synchronously before paint to avoid a flash at opacity 0.08
  useLayoutEffect(() => {
    if (!el || revealed) return;

    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setRevealed(true);
    }
  }, [el, revealed]);

  // Fallback: observe for scroll-into-view if not already visible
  useEffect(() => {
    if (revealed || !el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, revealed, el]);

  return { ref, revealed };
}
